// Global audio context — one active post at a time across all pages
// Also tracks volume globally so the slider doesn't reset on navigation.

import React, { createContext, useContext, useRef, useState, useCallback } from 'react';

interface AudioContextValue {
  activePostId: string | null;
  setActivePostId: (id: string | null) => void;
  volume: number;
  setVolume: (v: number) => void;
  registerStopCallback: (postId: string, stop: () => void) => void;
  deregisterStopCallback: (postId: string) => void;
  /** Call the active card's play/pause toggle */
  togglePlayCallback: React.MutableRefObject<(() => void) | null>;
}

const Ctx = createContext<AudioContextValue | null>(null);

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const [activePostId, setActivePostIdState] = useState<string | null>(null);
  const [volume, setVolume] = useState(0.8);
  const stopCallbacks = useRef<Map<string, () => void>>(new Map());
  const togglePlayCallback = useRef<(() => void) | null>(null);

  const setActivePostId = useCallback((id: string | null) => {
    setActivePostIdState(prev => {
      if (prev && prev !== id) {
        stopCallbacks.current.get(prev)?.();
      }
      return id;
    });
  }, []);

  const registerStopCallback = useCallback((postId: string, stop: () => void) => {
    stopCallbacks.current.set(postId, stop);
  }, []);

  const deregisterStopCallback = useCallback((postId: string) => {
    stopCallbacks.current.delete(postId);
  }, []);

  return (
    <Ctx.Provider value={{ activePostId, setActivePostId, volume, setVolume, registerStopCallback, deregisterStopCallback, togglePlayCallback }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAudio(): AudioContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAudio must be used inside <AudioProvider>');
  return ctx;
}
